import { post } from '../../../api/baseUrl';
import { useDispatch, useSelector } from 'react-redux';
import {
  type ClinicTO,
  type TenancyTO,
  type UpdatePreferencesPayload,
} from '../../../lib';
import type { RootState } from '../../../store';
import { setPreferences } from '../../../store/ai2';
import { useGlobalLoading } from '../../../context';

interface Tag {
  id: number;
  name: string;
  color?: string;
}

// Extend ClinicTO to include allTags
interface ClinicWithTags extends Omit<ClinicTO, 'tags'> {
  allTags?: Tag[];
  tags?: Tag[]; // Override the string[] type from ClinicTO since we're working with Tag objects
}

interface TenancyFeatures {
  hiddenTags: (string | number)[];
  [key: string]: any;
}

interface ExtensionRootState {
  clinicianProfile: {
    clinics: ClinicWithTags[];
    selectedTenancy: TenancyTO & { features?: TenancyFeatures };
  };
}

interface PostTagsPreferencesParams {
  selectedTags: string[];
}

export const useUpdateTagsPreferences = () => {
  const { setLoadingPreferences } = useGlobalLoading();
  const dispatch = useDispatch();
  const { clinics, selectedTenancy } = useSelector(
    (state: ExtensionRootState) => state.clinicianProfile,
  );
  const { allTags, tags, hiddenTags } = useSelector(
    (state: RootState) => state.preferences,
  );

  const updateTagsPreferences = async ({
    selectedTags,
  }: PostTagsPreferencesParams) => {
    if (!selectedTenancy) return;
    try {
      setLoadingPreferences(true);
      const body: UpdatePreferencesPayload = {
        tenancyId: selectedTenancy.tenancy_id,
        preferenceType: 'hiddenTags',
        preferenceValues: selectedTags,
      };
      const { data } = await post('/updatePreferences', body);
      if (data.data.success) {
        dispatch(
          setPreferences({
            hiddenTags: selectedTags,
            tags:
              tags?.filter((tag) => !selectedTags.includes(tag.shortName)) ||
              [],
          }),
        );
      }
    } catch (error) {
      console.error('Error updating tags preferences:', error);
      throw error;
    } finally {
      setLoadingPreferences(false);
    }
  };

  return {
    updateTagsPreferences,
    clinics,
    selectedTenancy,
    allTags: allTags || [],
    hiddenTags: hiddenTags || [],
  };
};
